import { forwardRef } from 'react';
import type { ExposedPopoverContainerProps } from './BasePopover';
import { BasePopover } from './BasePopover';
import type { PopoverContentProps } from './PopoverContent';
import { ZINDEX } from '@utils/constants';

export interface PopoverProps
  extends ExposedPopoverContainerProps,
    Omit<PopoverContentProps, 'children'> {
  text: PopoverContentProps['children'];
}

/**
 * A floating container used to display additional information and actions
 * about content.
 *
 * {@link https://zeroheight.com/76c439891/p/535ef1-popover Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-popover--basic-popover Code documentation}
 *
 * @example
 * <Popover title="A popover" text="A message content">
 *   <Text>Click me</Text>
 * </Popover>
 */
export const Popover = forwardRef<HTMLElement, PopoverProps>(
  ({ text, children, ...props }: PopoverProps, ref): JSX.Element => (
    <BasePopover {...props} ref={ref} content={text}>
      {children}
    </BasePopover>
  ),
);

Popover.displayName = 'Popover';

Popover.defaultProps = {
  zIndex: ZINDEX.POPOVER,
  placement: 'top',
  appendTo: () => document.body,
};
